import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Play } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { api, apiError } from "@/lib/api";
import {
  DEFAULT_FILTERS,
  ScanProgressPanel,
  SearchProfilesPanel,
  SourceMarketPanel,
  TradingFiltersPanel,
  VehicleFiltersPanel,
} from "@/components/scan/ScanPanels";

export default function NewScan() {
  const navigate = useNavigate();
  const [reference, setReference] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [profiles, setProfiles] = useState([]);
  const [profileName, setProfileName] = useState("");
  const [scan, setScan] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Referentiedata en zoekprofielen zijn eenmalig nodig bij het openen van de pagina.
    api
      .get("/reference")
      .then((r) => setReference(r.data))
      .catch((e) => console.warn("Referentiedata laden mislukt:", apiError(e)));
    loadProfiles();
  }, []);

  const loadProfiles = () =>
    api
      .get("/search-profiles")
      .then((r) => setProfiles(r.data))
      .catch((e) => console.warn("Zoekprofielen laden mislukt:", apiError(e)));

  useEffect(() => {
    if (!scan || ["COMPLETED", "ERROR"].includes(scan.status)) return;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get(`/scans/${scan.id}`);
        setScan(data);
        if (data.status === "COMPLETED") {
          clearInterval(timer);
        }
      } catch {
        clearInterval(timer);
      }
    }, 900);
    return () => clearInterval(timer);
  }, [scan]);

  const set = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const payload = useMemo(() => {
    const clean = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v === null) return;
      clean[k] = v;
    });
    return clean;
  }, [filters]);

  const startScan = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/scans", { filters: payload, profile_name: profileName || null });
      setScan(data);
    } catch (e) {
      setError(apiError(e));
    }
    setBusy(false);
  };

  const saveProfile = async () => {
    if (!profileName.trim()) {
      setError("Geef het zoekprofiel een naam.");
      return;
    }
    try {
      await api.post("/search-profiles", { name: profileName.trim(), filters: payload });
      setProfileName("");
      loadProfiles();
    } catch (e) {
      setError(apiError(e));
    }
  };

  const runProfile = async (p) => {
    const { data } = await api.post("/scans", { filters: p.filters, profile_name: p.name });
    setScan(data);
  };

  const duplicateProfile = async (id) => {
    await api.post(`/search-profiles/${id}/duplicate`);
    loadProfiles();
  };

  const deleteProfile = async (id) => {
    await api.delete(`/search-profiles/${id}`);
    loadProfiles();
  };

  const sources = (reference?.sources || []).filter((s) => s.key !== "manual_ingest");
  const manual = (reference?.sources || []).find((s) => s.key === "manual_ingest");
  const providerIssues = (scan?.provider_results || []).filter((p) => p.status !== "OK");

  return (
    <AppShell
      title="Nieuwe Scan"
      subtitle="Selecteer voertuig, bronmarkt en handelsdrempels — scan start binnen 30 seconden"
      actions={
        <button
          type="button"
          onClick={startScan}
          disabled={busy}
          data-testid="scan-submit-button"
          className="flex items-center gap-2 rounded-sm bg-blue-600 px-3.5 py-1.5 text-[12.5px] font-semibold text-white transition-colors duration-150 hover:bg-blue-700 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
          Scan Handelskansen
        </button>
      }
    >
      {error && (
        <div className="mb-3 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-800" data-testid="scan-error">
          {error}
        </div>
      )}

      {scan && (
        <ScanProgressPanel
          scan={scan}
          providerIssues={providerIssues}
          onViewResults={() => navigate(`/handelskansen?scan=${scan.id}`)}
        />
      )}

      <div className="grid gap-2 xl:grid-cols-[2fr_1fr]">
        <div className="space-y-2">
          <VehicleFiltersPanel filters={filters} set={set} setFilters={setFilters} reference={reference} />
          <SourceMarketPanel sources={sources} manual={manual} filters={filters} set={set} />
        </div>

        <div className="space-y-2">
          <TradingFiltersPanel filters={filters} set={set} />
          <SearchProfilesPanel
            profiles={profiles}
            profileName={profileName}
            setProfileName={setProfileName}
            onSave={saveProfile}
            onApply={(p) => setFilters({ ...DEFAULT_FILTERS, ...p.filters })}
            onRun={runProfile}
            onDuplicate={duplicateProfile}
            onDelete={deleteProfile}
          />
        </div>
      </div>
    </AppShell>
  );
}
